import { useQuery, WatchQueryFetchPolicy } from '@apollo/client';
import { GET_EV_CHARGING_PRICINGS } from '@queries/getEVChargingPricings';
import { useMemo } from 'react';
import {
  ChargingStation,
  Query,
  QueryGetEvChargingPricingsArgs,
} from 'types/generatedSchemaTypes';
import { getLang } from 'utils/commonUtils';
import useGetAvailableCustomerSegments from './useGetAvailableCustomerSegments';

const useEVChargingPricingInfo = ({
  chargingStations,
  fetchPolicy = 'cache-and-network',
}: {
  chargingStations: ChargingStation[];
  fetchPolicy?: WatchQueryFetchPolicy;
}) => {
  const lang = getLang();

  const { data: EVChargingPricings, loading: EVChargingPricingsLoading } =
    useQuery<Query, QueryGetEvChargingPricingsArgs>(GET_EV_CHARGING_PRICINGS, {
      variables: {
        locationIds: chargingStations.map(
          (station) => station.chargingLocationId
        ),
        lang: lang,
      },
      skip: !chargingStations?.length,
      fetchPolicy,
    });

  const { availableSegments, isPrivateOnlyWithNoAccess } =
    useGetAvailableCustomerSegments(EVChargingPricings?.getEVChargingPricings);

  return useMemo(
    () => ({
      EVChargingPricings: EVChargingPricings?.getEVChargingPricings,
      EVChargingPricingsLoading,
      pricingSegmentsEv: availableSegments,
      /** Every charging segment is private and the user has access to none */
      isPrivateOnlyWithNoAccess,
    }),
    [
      EVChargingPricings?.getEVChargingPricings,
      EVChargingPricingsLoading,
      availableSegments,
      isPrivateOnlyWithNoAccess,
    ]
  );
};

export default useEVChargingPricingInfo;
